import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { UIButton, UIIcon } from "../../../../ui";
import { clx } from "../../../../../utils/Helpers";
import { isSuperAdmin } from "../../../../../utils/Permissions";
import EventPayrollApplicantPayrollView from "./OneApplicant";
import EventPayrollBulkPayrollReportView from "./bulkApplicants";
import { EventPayrollExportModal, EventPayrollRevertConfirmModal } from "./Modals";

import "./EventPayrollGeneratePayroll.scss";

const EventPayrollGeneratePayrollPage = ({
  loading,
  data,
  isSingle,
  applicantCount,
  labels,
  shared,
  startDate,
  endDate,
  canAdd,
  canEdit,
  onStartDateChange,
  onEndDateChange,
  onClose,
  onAddDeduction,
  onAddReward,
  onRevertDeductionEntry,
  onRevertRewardEntry,
  onRevertAllDeductions,
  onRevertAllRewards,
  onExportReport,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [revertModal, setRevertModal] = useState({
    open: false,
    variant: "deduction",
    scope: "all",
    entry: null,
  });
  const showStcNumber = isSuperAdmin();
  const revertIcon = useMemo(
    () => <UIIcon name={lang === "ar" ? "undo2" : "redo2"} size={16} decorative />,
    [lang],
  );

  const handleOpenExport = useCallback(() => {
    setIsExportModalOpen(true);
  }, []);

  const handleCloseExport = useCallback(() => {
    setIsExportModalOpen(false);
  }, []);

  const handleConfirmExport = useCallback(
    (selectedFields, fields) => {
      setIsExportModalOpen(false);
      onExportReport?.(selectedFields, fields);
    },
    [onExportReport],
  );

  const openRevertModal = useCallback((variant, scope, entry = null) => {
    setRevertModal({ open: true, variant, scope, entry });
  }, []);

  const closeRevertModal = useCallback(() => {
    setRevertModal((prev) => ({ ...prev, open: false }));
  }, []);

  const handleConfirmRevert = useCallback(() => {
    const { variant, scope, entry } = revertModal;
    closeRevertModal();

    if (variant === "deduction") {
      if (scope === "all") onRevertAllDeductions?.();
      else onRevertDeductionEntry?.(entry);
    } else if (scope === "all") {
      onRevertAllRewards?.();
    } else {
      onRevertRewardEntry?.(entry);
    }
  }, [
    revertModal,
    closeRevertModal,
    onRevertAllDeductions,
    onRevertAllRewards,
    onRevertDeductionEntry,
    onRevertRewardEntry,
  ]);

  const hasManualDeductions =
    isSingle &&
    (data?.deductions?.entries || []).some((entry) => entry.type === "manual");

  const hasRewards = isSingle && (data?.rewards?.entries || []).length > 0;

  const footerMessage = isSingle
    ? labels.footer.single
    : labels.footer.bulk.replace("{count}", String(applicantCount));

  return (
    <div
      className={clx(
        "event-payroll-generate-page",
        isSingle
          ? "event-payroll-generate-page--single"
          : "event-payroll-generate-page--bulk",
        loading && "event-payroll-generate-page--loading",
      )}
    >
      {data?.mode === "single" ? (
        <EventPayrollApplicantPayrollView
          data={data}
          labels={labels}
          shared={shared}
          showStcNumber={showStcNumber}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          onAddDeduction={canAdd ? onAddDeduction : undefined}
          onAddReward={canAdd ? onAddReward : undefined}
          onRevertDeductionEntry={
            canEdit ? (entry) => openRevertModal("deduction", "single", entry) : undefined
          }
          onRevertRewardEntry={
            canEdit ? (entry) => openRevertModal("reward", "single", entry) : undefined
          }
          canRevert={canEdit}
        />
      ) : null}

      {data?.mode === "bulk" ? (
        <EventPayrollBulkPayrollReportView
          data={data}
          applicantCount={applicantCount}
          labels={labels}
          shared={shared}
          showStcNumber={showStcNumber}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
        />
      ) : null}

      <footer className="event-payroll-generate-page__footer">
        <div className="event-payroll-generate__footer">
          <UIButton
            type="button"
            variant="outline"
            size="large"
            onClick={onClose}
          >
            {labels.actions.back}
          </UIButton>

          <div className="event-payroll-generate__footer-actions">
            {isSingle && canEdit ? (
              <>
                <UIButton
                  type="button"
                  variant="outline"
                  size="large"
                  startIcon={revertIcon}
                  onClick={() => openRevertModal("deduction", "all")}
                  disabled={!canEdit || !hasManualDeductions}
                >
                  {labels.actions.revertDeduction}
                </UIButton>
                <UIButton
                  type="button"
                  variant="outline"
                  size="large"
                  startIcon={revertIcon}
                  onClick={() => openRevertModal("reward", "all")}
                  disabled={!canEdit || !hasRewards}
                >
                  {labels.actions.revertReward}
                </UIButton>
              </>
            ) : null}

            {!isSingle ? (
              <span className="event-payroll-generate__footer-message text-body-sm">
                {footerMessage}
              </span>
            ) : null}

            <UIButton
              type="button"
              variant="primary"
              size="large"
              startIcon={
                <UIIcon
                  name="exportSheet"
                  width={16}
                  height={16}
                  color="currentColor"
                  strokeWidth={1.5}
                  decorative
                />
              }
              onClick={handleOpenExport}
            >
              {labels.actions.exportReport}
            </UIButton>
          </div>
        </div>
      </footer>

      <EventPayrollExportModal
        open={isExportModalOpen}
        data={data}
        isSingle={isSingle}
        onClose={handleCloseExport}
        onConfirm={handleConfirmExport}
      />

      <EventPayrollRevertConfirmModal
        open={revertModal.open}
        variant={revertModal.variant}
        scope={revertModal.scope}
        labels={labels.revertConfirm}
        shared={shared}
        onClose={closeRevertModal}
        onConfirm={handleConfirmRevert}
      />
    </div>
  );
};

EventPayrollGeneratePayrollPage.propTypes = {
  loading: PropTypes.bool,
  data: PropTypes.object,
  isSingle: PropTypes.bool,
  applicantCount: PropTypes.number,
  labels: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  canAdd: PropTypes.bool,
  canEdit: PropTypes.bool,
  onStartDateChange: PropTypes.func,
  onEndDateChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onAddDeduction: PropTypes.func,
  onAddReward: PropTypes.func,
  onRevertDeductionEntry: PropTypes.func,
  onRevertRewardEntry: PropTypes.func,
  onRevertAllDeductions: PropTypes.func,
  onRevertAllRewards: PropTypes.func,
  onExportReport: PropTypes.func,
};

EventPayrollGeneratePayrollPage.defaultProps = {
  loading: false,
  data: null,
  isSingle: false,
  applicantCount: 0,
  startDate: "",
  endDate: "",
  canAdd: true,
  canEdit: true,
  onStartDateChange: undefined,
  onEndDateChange: undefined,
  onAddDeduction: undefined,
  onAddReward: undefined,
  onRevertDeductionEntry: undefined,
  onRevertRewardEntry: undefined,
  onRevertAllDeductions: undefined,
  onRevertAllRewards: undefined,
  onExportReport: undefined,
};

export default EventPayrollGeneratePayrollPage;
